// src/pages/WatchlistPage.jsx
import React, { useState } from 'react';
import { Plus, X, Coins, BarChart3, DollarSign, Package, Gem } from 'lucide-react';
import TradingViewWidget from '../widgets/TradingViewWidget';

// Define categories for watchlist with proper icons
// Adjusted colors for better visibility in light mode
const categories = [
  { key: 'forex', label: 'Forex', icon: DollarSign, color: 'bg-blue-100 text-blue-600 dark:bg-blue-600/20 dark:text-blue-400' },
  { key: 'commodities', label: 'Commodities', icon: Package, color: 'bg-green-100 text-green-600 dark:bg-green-600/20 dark:text-green-400' },
  { key: 'metals', label: 'Metals', icon: Gem, color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-600/20 dark:text-yellow-400' },
  { key: 'indices', label: 'Indices', icon: BarChart3, color: 'bg-purple-100 text-purple-600 dark:bg-purple-600/20 dark:text-purple-400' },
  { key: 'crypto', label: 'Crypto', icon: Coins, color: 'bg-orange-100 text-orange-600 dark:bg-orange-600/20 dark:text-orange-400' },
];

// Initial watchlist data
const initialWatchlist = {
  forex: ['EURUSD', 'GBPJPY', 'AUDUSD'],
  commodities: ['OIL', 'NATGAS'],
  metals: ['GOLD', 'SILVER'],
  indices: ['SPX', 'NAS100', 'DJI'],
  crypto: ['BTCUSD', 'ETHUSD', 'SOLUSD'],
};

export default function WatchlistPage() {
  const [watchlist, setWatchlist] = useState(initialWatchlist);
  const [activeCategory, setActiveCategory] = useState('forex');
  const [newItem, setNewItem] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);

  const handleAddItem = async (e) => {
    e.preventDefault();
    setIsAddingItem(true);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const upperItem = newItem.toUpperCase();
    if (upperItem && !watchlist[activeCategory].includes(upperItem)) {
      setWatchlist(prev => ({
        ...prev,
        [activeCategory]: [...prev[activeCategory], upperItem],
      }));
    }
    setNewItem('');
    setIsAddingItem(false);
  };

  const handleRemoveItem = async (itemToRemove) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setWatchlist(prev => ({
      ...prev,
      [activeCategory]: prev[activeCategory].filter(item => item !== itemToRemove),
    }));
  };

  return (
    // Page Background: Light slate / Dark gray
    <div className="h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 max-w-6xl mx-auto transition-colors duration-300 overflow-y-auto overflow-x-hidden">
      
      {/* Sticky compact bar — hero title scrolls beneath it */}
      <header className="sticky top-0 z-10 -mx-4 md:-mx-6 px-4 md:px-6 pt-12 md:pt-3 pb-3 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Watchlist</h1>
      </header>

      <div className="pt-8 pb-6 mb-4 flex flex-col items-center">
          <h1 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 dark:from-blue-400 dark:via-purple-400 dark:to-purple-500 mb-4 tracking-tight">
            Watchlist
          </h1>
          
          <p className="text-gray-600 dark:text-gray-400 text-center max-w-lg text-xl leading-relaxed">
            Track your favorite assets across different markets
          </p>
      </div>

      <div className="space-y-8">
        
        {/* --- Category Tabs Section --- */}
        <section>
          {/* Tabs Container: White card in light mode */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 p-2 shadow-sm dark:shadow-none">
            <div className="flex flex-wrap gap-2">
              {categories.map(({ key, label, icon: Icon, color }) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`flex-1 min-w-[120px] p-3 rounded-xl font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                    activeCategory === key
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-bold shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* --- Add Item Section --- */}
        <section>
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 p-6 shadow-sm dark:shadow-none">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Plus size={20} className="text-blue-500 dark:text-blue-400" />
              Add New Item
            </h2>
            <form onSubmit={handleAddItem} className="flex gap-3">
              <div className="relative flex-grow">
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder={`Add to ${categories.find(c => c.key === activeCategory)?.label} (e.g., TSLA)`}
                  className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-4 py-3 pl-12 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base placeholder-gray-400 dark:placeholder-gray-400"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  {(() => {
                    const category = categories.find(c => c.key === activeCategory);
                    return <category.icon size={18} className="text-gray-400" />;
                  })()}
                </div>
              </div>
              <button 
                type="submit" 
                disabled={isAddingItem || !newItem.trim()}
                className="bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingItem ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Plus size={20} />
                )}
              </button>
            </form>
          </div>
        </section>

        {/* --- Watchlist Items Grid --- */}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {watchlist[activeCategory].map(item => (
              // Card: White in light mode with subtle border
              <div key={item} className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none hover:shadow-md dark:hover:shadow-none transition-all duration-300 hover:transform hover:scale-[1.02]">
                {/* Chart Container */}
                <div className="h-48 border-b border-gray-100 dark:border-gray-700/50">
                  <TradingViewWidget symbol={item} />
                </div>
                
                {/* Item Footer */}
                <div className="p-4 flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">{item}</span>
                  <button
                    onClick={() => handleRemoveItem(item)}
                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-600/10"
                    aria-label={`Remove ${item}`}
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Empty state */}
          {watchlist[activeCategory].length === 0 && (
            <div className="bg-gray-100 dark:bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-200 dark:border-gray-700/50 p-12 text-center">
              <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus size={24} className="text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">No items in watchlist</h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                Add items to your {categories.find(c => c.key === activeCategory)?.label} watchlist to start tracking them.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
